/**
 * <s-signup-popup> — opens its <dialog> once per visitor after `delay` seconds.
 *
 * Attributes:
 *   delay            — seconds to wait after arrival (default 15)
 *   storage-key      — localStorage key that records the visitor has seen it
 *   data-design-mode — present in the theme editor; the pop-up never auto-opens there
 */
class SSignupPopup extends HTMLElement {
  #timer = null;

  connectedCallback() {
    if (this.hasAttribute('data-design-mode')) return;

    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    const key = this.getAttribute('storage-key') || 'signup-popup-seen';
    if (this.#hasSeen(key)) return;

    // A visitor returning from a sign-up submission has already joined, so never interrupt them.
    if (new URLSearchParams(window.location.search).has('customer_posted')) {
      this.#remember(key);
      return;
    }

    const seconds = Number(this.getAttribute('delay'));
    const delay = Number.isFinite(seconds) && seconds >= 0 ? seconds * 1000 : 15000;
    this.#schedule(dialog, key, delay);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
  }

  #schedule(dialog, key, delay) {
    this.#timer = setTimeout(() => {
      // Wait politely if the cart, search or menu is open rather than stacking modals.
      if (document.querySelector('dialog:modal')) {
        this.#schedule(dialog, key, 3000);
        return;
      }
      if (dialog.open) return;
      dialog.showModal();
      this.#remember(key);
    }, delay);
  }

  #hasSeen(key) {
    try {
      return window.localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  }

  #remember(key) {
    try {
      window.localStorage.setItem(key, '1');
    } catch {
      // Storage can be blocked (private mode); the pop-up then simply shows per visit.
    }
  }
}

if (!customElements.get('s-signup-popup')) customElements.define('s-signup-popup', SSignupPopup);
